import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectedCardSelector } from 'store/slices/userCard';

import type { ReservationHoldMessage } from '../api/reservationHold';
import { RESERVATION_ROOT } from '../routes';
import type { ReservationCardStatus } from '../types';
import { isReservationFlowActive } from './reservationFlags';
import { getReservationEntryPath, markReservationHold, shouldHandOverToReservation } from './reservationHold';

/**
 * Returns a stable handler that hands the player over to the reservation flow when a payload
 * reports a hold awaiting verification. Used for the `PaymentStatus` hub message and for the
 * `payments/session-state` response alike, so both sources follow exactly the same rule.
 *
 * Everything is read through refs: callers invoke it from long-lived subscriptions and from promises
 * that resolve after several renders.
 *
 * Returns `true` when the payload was a hold (the caller should stop its own status handling).
 */
export const useReservationHoldHandOver = (): ((payload?: ReservationHoldMessage | null) => boolean) => {
  const isActive = useAppSelector(isReservationFlowActive);
  const { selectedCard } = useAppSelector(selectedCardSelector);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Read through this feature's own enum: the numeric values are the backend's, but the type in
  // `store/models` belongs to `kyc_card_verification` and must not leak in here.
  const cardStatus = (selectedCard?.kyc_verification_status as ReservationCardStatus | null) ?? null;

  const stateRef = useRef({ isActive, cardStatus, navigate, pathname });
  stateRef.current = { isActive, cardStatus, navigate, pathname };

  return useCallback((payload?: ReservationHoldMessage | null) => {
    const { isActive: isFlowActive, cardStatus: status, navigate: navigateTo, pathname: path } = stateRef.current;

    if (!isFlowActive || !shouldHandOverToReservation(payload, status)) {
      return false;
    }

    markReservationHold();

    if (!path.startsWith(RESERVATION_ROOT)) {
      navigateTo(getReservationEntryPath(), { replace: true });
    }

    return true;
  }, []);
};
